import { Alert, Button, Card, Space, Tag, Typography } from 'antd';
import { SalesforceSupplierSearchResult } from 'sb/api/vendor';
import { VendorAdmin } from 'sb/models/prisma/VendorAdmin';

type ReconciliationActionsCardProps = {
  requiresOverride: boolean;
  actionModeLabel: string;
  linkStatusTagColor: string;
  linkStatusLabel: string;
  vendor?: VendorAdmin;
  selectedSalesforceSupplier?: SalesforceSupplierSearchResult;
  isMissingSelection: boolean;
  vendorLinkedToDifferentAccount: boolean;
  salesforceAccountLinkedToDifferentVendor: boolean;
  salesforceAccountLinkedVendorLookupLoading: boolean;
  salesforceAccountLinkedVendorId?: number;
  salesforceAccountLinkedVendorName?: string;
  isActionLoading: boolean;
  onLinkSelectedRecords: () => void;
  onOpenCreateRecordModal: () => void;
  onOpenOverrideModal: () => void;
  onOpenCreateSalesforceAccountModal: () => void;
};

export const ReconciliationActionsCard: React.FC<ReconciliationActionsCardProps> = ({
  requiresOverride,
  actionModeLabel,
  linkStatusTagColor,
  linkStatusLabel,
  vendor,
  selectedSalesforceSupplier,
  isMissingSelection,
  vendorLinkedToDifferentAccount,
  salesforceAccountLinkedToDifferentVendor,
  salesforceAccountLinkedVendorLookupLoading,
  salesforceAccountLinkedVendorId,
  salesforceAccountLinkedVendorName,
  isActionLoading,
  onLinkSelectedRecords,
  onOpenCreateRecordModal,
  onOpenOverrideModal,
  onOpenCreateSalesforceAccountModal
}) => (
  <Card className="mt-5 bg-gray-50" title="Reconciliation Actions">
    <div className="mb-4 rounded border border-gray-200 bg-white p-4">
      <Typography.Title level={5} className="!mb-3">
        Reconciliation Summary
      </Typography.Title>
      <Space size={[8, 8]} wrap className="mb-2">
        <Tag color={requiresOverride ? 'orange' : 'blue'}>{actionModeLabel}</Tag>
        <Tag color={linkStatusTagColor}>{linkStatusLabel}</Tag>
      </Space>
      <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
        Vantage vendor:{' '}
        {vendor ? (
          <span className="font-medium">
            #{vendor.id} - {vendor.name}
          </span>
        ) : (
          'None selected'
        )}
      </Typography.Paragraph>
      <Typography.Paragraph className="!mb-1 text-sm text-gray-700">
        Salesforce account:{' '}
        {selectedSalesforceSupplier ? (
          <span className="font-medium">
            {selectedSalesforceSupplier.Name} ({selectedSalesforceSupplier.Id})
          </span>
        ) : (
          'None selected'
        )}
      </Typography.Paragraph>
      <Typography.Paragraph className="!mb-0 text-sm text-gray-700">
        Current Vantage link: {vendor?.salesforce_id || 'Not linked'}
      </Typography.Paragraph>
    </div>

    {isMissingSelection && (
      <Alert
        className="mb-3"
        type="warning"
        showIcon
        message="Selection required"
        description="Choose one Vantage vendor and one Salesforce supplier account before linking."
      />
    )}

    {vendorLinkedToDifferentAccount && (
      <Alert
        className="mb-3"
        type="error"
        showIcon
        message="Vantage vendor is already linked"
        description={`This vendor is currently linked to Salesforce account ${vendor?.salesforce_id}.`}
      />
    )}

    {salesforceAccountLinkedToDifferentVendor && (
      <Alert
        className="mb-3"
        type="error"
        showIcon
        message="Salesforce account is already linked"
        description={
          salesforceAccountLinkedVendorLookupLoading
            ? 'Checking current account ownership...'
            : `This Salesforce account is currently linked to vendor #${salesforceAccountLinkedVendorId} (${
                salesforceAccountLinkedVendorName || 'Unknown'
              }).`
        }
      />
    )}

    <Space wrap>
      <Button loading={isActionLoading} onClick={onOpenCreateRecordModal}>
        Create Vantage vendor
      </Button>
      <Button type="primary" loading={isActionLoading} disabled={isMissingSelection} onClick={onLinkSelectedRecords}>
        Link selected records
      </Button>
      <Button disabled={!requiresOverride} loading={isActionLoading} onClick={onOpenOverrideModal}>
        Override existing link
      </Button>
      <Button disabled={!vendor} loading={isActionLoading} onClick={onOpenCreateSalesforceAccountModal}>
        Create Salesforce account
      </Button>
    </Space>
    <Typography.Paragraph className="!mb-0 mt-3 text-sm text-gray-500">
      Reconciliation actions are live and will refresh both panes while preserving your current selection context.
    </Typography.Paragraph>
  </Card>
);
